<template>
  <div class="admin-questionnaire-detail-wrap">
    <main v-if="item" class="page">

      <p class="breadcrumb">
        SEQ / Admin /
        <router-link to="/admin/questionnaires" class="breadcrumb-link">Questionnaires</router-link>
        / {{ item.name }}
      </p>

      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">{{ item.name }}</h1>
          <p v-if="item.description" class="page-subtitle">{{ item.description }}</p>
        </div>
        <div class="page-header-actions">
          <button
            type="button"
            class="btn btn-secondary"
            :class="{ 'btn-mode-active': editMode }"
            :disabled="item.is_locked"
            :title="lockTooltip"
            @click="onToggleEditMode"
          >{{ editMode ? 'Done editing' : 'Edit' }}</button>
          <button
            type="button"
            class="btn btn-secondary btn-with-icon"
            title="Export this questionnaire (structure + authored text in all four languages) as JSON — for localization handoff or comparison"
            @click="onExportJson"
          >
            <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
              <path d="M8 2v8m0 0l-3-3m3 3l3-3M3 13h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
            Export JSON
          </button>
          <button
            type="button"
            class="btn btn-secondary btn-with-icon"
            title="Translation upload temporarily disabled during the data model migration. Will be re-enabled in Step 3."
            disabled
          >
            <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
              <path d="M8 11V3m0 0L5 6m3-3l3 3M3 13h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
            Upload translations
          </button>
          <input
            ref="translationFileInput"
            type="file"
            accept=".json,application/json"
            class="hidden-file-input"
            @change="onFileSelected"
          >
          <button
            type="button"
            class="btn btn-danger-outline"
            :disabled="item.is_locked"
            :title="lockTooltip"
            @click="onDeleteClick"
          >Delete</button>
        </div>
      </div>

      <div class="meta-strip">
        <div class="meta-item">
          <span class="meta-label">Slug</span>
          <span class="meta-value mono">{{ item.slug }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Status</span>
          <span class="meta-value">
            <span class="status-pill" :class="`status-${item.status}`">{{ item.status }}</span>
          </span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Lock</span>
          <span class="meta-value lock-value">
            <template v-if="item.is_locked">🔒 Locked</template>
            <template v-else><span class="dim">Unlocked</span></template>
          </span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Created</span>
          <span class="meta-value">{{ formatDate(item.created_at) }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Updated</span>
          <span class="meta-value">{{ formatDate(item.updated_at) }}</span>
        </div>

        <button
          v-if="editMode"
          type="button"
          class="meta-edit-link"
          title="Edit name, description, and status"
          @click="onEditClick"
        >
          <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
            <path d="M11.2 2.4l2.4 2.4L5.6 12.8H3.2v-2.4L11.2 2.4z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
          </svg>
          Edit details
        </button>
      </div>

      <div class="translation-strip" role="group" aria-label="Translation completeness">
        <span class="translation-label">Translations:</span>
        <template v-for="(loc, idx) in LOCALES" :key="loc">
          <span v-if="idx > 0" class="translation-sep" aria-hidden="true">·</span>
          <button
            type="button"
            class="translation-pct"
            :class="[
              `pct-${completenessBucket(completeness[loc])}`,
              { 'is-active': filterMissingLocale === loc },
            ]"
            :disabled="completeness[loc] >= 100"
            :aria-pressed="String(filterMissingLocale === loc)"
            :aria-label="completeness[loc] >= 100
              ? `${LOCALE_LABEL[loc]} fully translated`
              : `Filter to cards missing ${LOCALE_LABEL[loc]} translations`"
            :title="completeness[loc] >= 100
              ? `${LOCALE_LABEL[loc]} fully translated`
              : `Show cards missing ${LOCALE_LABEL[loc]} translations`"
            @click="onLocaleClick(loc)"
          >
            <span class="locale-code">{{ LOCALE_LABEL[loc] }}</span>
            <span class="locale-value">{{ completeness[loc] }}%</span>
          </button>
        </template>
      </div>

      <section class="section">
        <h2 class="section-title">Cards and sections</h2>

        <div v-if="filterMissingLocale" class="filter-banner" role="status">
          <span class="filter-banner-text">
            Showing cards with missing
            <strong>{{ filterLocaleLabel }}</strong>
            translations.
          </span>
          <button
            type="button"
            class="filter-banner-clear"
            @click="clearLocaleFilter"
          >Clear filter</button>
        </div>

        <p v-if="loadingContent" class="loading-state">Loading sections and items…</p>

        <div v-else-if="contentError" class="content-error">
          <p class="content-error-text">{{ contentError }}</p>
          <button
            type="button"
            class="btn btn-secondary"
            @click="onRetryContent"
          >Retry</button>
        </div>

        <QuestionnaireSectionList
          v-else
          :questionnaire-slug="item.slug"
          :locked="item.is_locked"
          :edit-mode="editMode"
          :filter-missing-locale="filterMissingLocale"
          @show-error="(msg) => showToast('error', msg)"
          @show-success="(msg) => showToast('success', msg)"
          @refetch-content="fetchContent"
        />
      </section>

      <section class="section">
        <h2 class="section-title">Revisions</h2>
        <p class="section-intro">
          A revision is an immutable snapshot of this questionnaire: every item, its
          order, and all four languages, frozen. Participants answer against a revision
          rather than the live library, so editing a question later can never change the
          meaning of data already collected.
        </p>
        <QuestionnaireRevisionsPanel
          :questionnaire-slug="item.slug"
          @show-error="(msg) => showToast('error', msg)"
          @show-success="(msg) => showToast('success', msg)"
        />
      </section>

    </main>

    <main v-else-if="loading" class="page">
      <p class="breadcrumb">
        SEQ / Admin /
        <router-link to="/admin/questionnaires" class="breadcrumb-link">Questionnaires</router-link>
        / <span class="dim">{{ slug }}</span>
      </p>
      <p class="loading-state">Loading questionnaire…</p>
    </main>

    <main v-else class="page">
      <p class="breadcrumb">
        SEQ / Admin /
        <router-link to="/admin/questionnaires" class="breadcrumb-link">Questionnaires</router-link>
        / {{ fetchError ? 'Error' : 'Not found' }}
      </p>
      <h1 class="page-title">
        {{ fetchError ? 'Could not load questionnaire' : 'Questionnaire not found' }}
      </h1>
      <p class="page-subtitle">
        <template v-if="fetchError">{{ fetchError }}</template>
        <template v-else>
          No questionnaire matches the slug
          <code class="mono-inline">{{ slug }}</code>.
          It may have been deleted or never existed.
        </template>
      </p>
      <p>
        <router-link to="/admin/questionnaires" class="back-link">
          ← Back to all questionnaires
        </router-link>
      </p>
    </main>

    <QuestionnaireFormModal
      v-if="showEdit && item"
      mode="edit"
      :initial="item"
      :server-error="editError"
      @submit="onEditSubmit"
      @cancel="onEditCancel"
    />

    <div
      v-if="showDelete && item"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-title"
      @click.self="onDeleteCancel"
    >
      <div class="modal modal-confirm">
        <h2 id="delete-title" class="modal-title">Delete {{ item.name }}?</h2>
        <p class="modal-text">
          This cannot be undone. To confirm, type the slug
          <code class="confirm-target">{{ item.slug }}</code>
          exactly.
        </p>

        <input
          ref="confirmInput"
          v-model="confirmText"
          type="text"
          class="confirm-input"
          :class="{ 'matched': confirmMatches }"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          aria-label="Type the slug to confirm deletion"
          :placeholder="item.slug"
          :disabled="deleting"
          @keyup.enter="confirmMatches && !deleting && confirmDelete()"
        >

        <p
          v-if="deleteError"
          class="modal-error"
          role="alert"
        >{{ deleteError }}</p>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="deleting"
            @click="onDeleteCancel"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="deleting || !confirmMatches"
            @click="confirmDelete"
          >{{ deleting ? 'Deleting…' : 'Delete' }}</button>
        </div>
      </div>
    </div>

    <!-- Leave-editor confirm (when navigating away while in edit mode) -->
    <div
      v-if="showLeaveConfirm"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-title"
      @click.self="onCancelLeave"
    >
      <div class="modal modal-confirm">
        <h2 id="leave-title" class="modal-title">Leave editor?</h2>
        <p class="modal-text">Your changes are saved.</p>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="onCancelLeave"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            @click="onConfirmLeave"
          >Leave</button>
        </div>
      </div>
    </div>

    <!-- Upload translations preview -->
    <div
      v-if="uploadPreview && item"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-title"
      @click.self="onCancelUploadPreview"
    >
      <div class="modal modal-upload">
        <h2 id="upload-title" class="modal-title">Apply translation file?</h2>

        <p v-if="item.is_locked" class="modal-lock-note">
          This questionnaire is locked, but translation changes are permitted.
        </p>

        <p class="modal-text">
          This file contains updates for
          <strong>{{ uploadPreview.sections_count }}</strong>
          {{ uploadPreview.sections_count === 1 ? 'section' : 'sections' }} and
          <strong>{{ uploadPreview.cards_count }}</strong>
          {{ uploadPreview.cards_count === 1 ? 'card' : 'cards' }}.
          <br>
          Languages found:
          DE ({{ uploadPreview.language_counts.de }} filled),
          EN ({{ uploadPreview.language_counts.en }} filled),
          FR ({{ uploadPreview.language_counts.fr }} filled),
          IT ({{ uploadPreview.language_counts.it }} filled).
        </p>

        <p
          v-if="uploadPreview.skipped_sections.length > 0 || uploadPreview.skipped_cards.length > 0"
          class="modal-skipped"
        >
          Some entries in the file don't match this questionnaire and will be ignored:
          <span v-if="uploadPreview.skipped_sections.length > 0">
            sections {{ uploadPreview.skipped_sections.join(', ') }}.
          </span>
          <span v-if="uploadPreview.skipped_cards.length > 0">
            cards {{ uploadPreview.skipped_cards.join(', ') }}.
          </span>
        </p>

        <p
          v-if="uploadPreview.changes.length === 0"
          class="modal-no-changes"
        >No fields would change. The file's translations match what's already on this questionnaire.</p>

        <details v-else class="modal-details">
          <summary>{{ uploadPreview.changes.length }} {{ uploadPreview.changes.length === 1 ? 'field' : 'fields' }} would change</summary>
          <ul class="change-list">
            <li
              v-for="(c, i) in uploadPreview.changes.slice(0, 100)"
              :key="i"
              class="change-row"
            >
              <span class="change-scope">
                {{ c.scope === 'section' ? 'Section' : 'Card' }}
                <code class="mono-inline">{{ c.identifier }}</code>
              </span>
              <span class="change-field mono-inline">{{ c.path }} ({{ c.lang }})</span>
              <span class="change-action" :class="`action-${c.action}`">{{ c.action }}</span>
            </li>
            <li v-if="uploadPreview.changes.length > 100" class="change-row dim">
              … and {{ uploadPreview.changes.length - 100 }} more.
            </li>
          </ul>
        </details>

        <p class="modal-note">
          Only translation fields will be modified. Structure, codes, and ordering are unchanged.
        </p>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="onCancelUploadPreview"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="uploadPreview.changes.length === 0"
            @click="onApplyTranslations"
          >Apply translations</button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <transition name="toast-fade">
      <div
        v-if="toast"
        class="toast"
        :class="`toast-${toast.kind}`"
        role="status"
        aria-live="polite"
      >{{ toast.text }}</div>
    </transition>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import {
  findBySlug,
  removeQuestionnaire,
  replaceQuestionnaire,
  upsertQuestionnaire,
} from './_data/questionnaires.js';
import QuestionnaireFormModal from './QuestionnaireFormModal.vue';
import QuestionnaireSectionList from './QuestionnaireSectionList.vue';
import QuestionnaireRevisionsPanel from './QuestionnaireRevisionsPanel.vue';
import {
  buildQuestionnaireExport,
  previewTranslationApply,
  applyTranslationData,
  loadQuestionnaireData,
  loadQuestionnaireSections,
  loadQuestionnaireItems,
  itemsForQuestionnaire,
  cardsState,
  sectionsForQuestionnaire,
} from './_data/cards.js';
import {
  computeCompleteness,
  LOCALES,
} from '@/utils/translationCompleteness.js';

const LOCALE_LABEL = { en: 'EN', de: 'DE', fr: 'FR', it: 'IT' };

const dateFmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export default {
  name: 'AdminQuestionnaireDetail',
  components: {
    QuestionnaireFormModal,
    QuestionnaireSectionList,
    QuestionnaireRevisionsPanel,
  },
  data() {
    return {
      showEdit: false,
      editError: null,
      showDelete: false,
      deleting: false,
      deleteError: null,
      confirmText: '',
      loading: false,
      notFound: false,
      fetchError: null,
      uploadPreview: null, // { fileData, sections_count, cards_count, language_counts, changes, skipped_sections, skipped_cards }
      toast: null,         // { kind: 'error' | 'success' | 'info', text }
      toastTimer: null,
      editMode: false,
      // Used by beforeRouteLeave: when the user tries to navigate away
      // while in edit mode, we stash the next() callback here, show a
      // confirm dialog, then call next() (or next(false) to cancel) once
      // the user picks. Always null outside the confirm flow.
      showLeaveConfirm: false,
      pendingNext: null,
      // Sections + cards live in the cardsState reactive store; this
      // component just tracks the fetch lifecycle.
      loadingContent: false,
      contentError: null,
      // Translation-completeness filter. null = no filter; otherwise one
      // of 'en' | 'de' | 'fr' | 'it'. Component-local, not persisted —
      // reloads clear the filter (per spec).
      filterMissingLocale: null,
      // Exposed to the template.
      LOCALES,
      LOCALE_LABEL,
    };
  },
  watch: {
    slug: {
      immediate: true,
      handler(newSlug) {
        this.fetchItem(newSlug);
      },
    },
  },
  computed: {
    slug() {
      return this.$route.params.slug;
    },
    item() {
      return findBySlug(this.slug) || null;
    },
    lockTooltip() {
      return this.item && this.item.is_locked
        ? 'This questionnaire has submissions and cannot be edited. Duplicate it to create an editable copy.'
        : null;
    },
    confirmMatches() {
      return !!(this.item && this.confirmText === this.item.slug);
    },
    /**
     * Snapshot of the sections + cards belonging to this questionnaire,
     * read from the shared cardsState. Touched lengths so the computed
     * re-runs when content is added or removed.
     */
    questionnaireContent() {
      // Touch the reactive lengths so the computed re-runs on
      // add/remove and on the post-fetch refresh.
      // eslint-disable-next-line no-unused-vars
      const _s = cardsState.sections.length;
      // eslint-disable-next-line no-unused-vars
      const _i = cardsState.items.length;
      if (!this.item) return { sections: [], items: [] };
      const sections = sectionsForQuestionnaire(this.item.slug);
      const items = itemsForQuestionnaire(this.item.slug);
      return { sections, items };
    },
    /**
     * Whole-number per-locale percentages, computed from the content above
     * via the pure translationCompleteness utility. Returns 100 for every
     * locale when there's nothing to translate.
     */
    completeness() {
      return computeCompleteness(this.questionnaireContent);
    },
    /**
     * Human label for the active filter locale ("FR", "DE", …), used in
     * the banner above the cards list.
     */
    filterLocaleLabel() {
      return this.filterMissingLocale
        ? LOCALE_LABEL[this.filterMissingLocale]
        : '';
    },
  },
  methods: {
    /**
     * Color bucket for the percentage value. Used in template class binding.
     * The locale code itself stays neutral; only the percentage gets coloured.
     */
    completenessBucket(pct) {
      if (pct >= 100) return 'complete';
      if (pct >= 50) return 'partial';
      return 'low';
    },
    onLocaleClick(locale) {
      // Per the design discussion: 100% buttons are disabled (handled in
      // the template) so we don't need to filter to "missing X" when X
      // has no missing cards.
      if (this.filterMissingLocale === locale) {
        this.filterMissingLocale = null;
      } else {
        this.filterMissingLocale = locale;
      }
    },
    clearLocaleFilter() {
      this.filterMissingLocale = null;
    },

    async fetchItem(slug) {
      // GET /api/seq/questionnaires/:slug. Always refresh from the server
      // so the detail view is current even on direct URL load or refresh.
      // If the row is already in the local store (we came from the list),
      // it stays visible during the fetch — we only show the loading
      // state when there is nothing cached to render.
      if (!slug) return;
      this.loading = true;
      this.notFound = false;
      this.fetchError = null;
      try {
        const item = await apiService.fetchQuestionnaireBySlug(slug);
        upsertQuestionnaire(item);
        // Fire-and-forget the section/card load so the questionnaire
        // strip renders right away while the heavier content fetch is
        // still in flight. The section list shows its own spinner.
        this.fetchContent();
      } catch (e) {
        if (e?.response?.status === 404) {
          this.notFound = true;
        } else {
          const detail = e?.response?.data?.detail;
          this.fetchError = typeof detail === 'string'
            ? detail
            : 'Failed to load questionnaire from the server.';
        }
      } finally {
        this.loading = false;
      }
    },

    // Fetch sections and cards for the current questionnaire in parallel
    // and load them into the shared store. Page reload reverts any local
    // edits because the next mount re-fetches from the API.
    async fetchContent() {
      if (!this.item || !this.item.slug) return;
      const slug = this.item.slug;
      this.loadingContent = true;
      this.contentError = null;
      try {
        // Junction Step 3 cutover (global sections): items come from
        // /items, sections are no longer fetched per-questionnaire —
        // the legacy /questionnaires/:slug/sections endpoint queries a
        // dropped column (questionnaire_id) and 500s post-migration.
        // The questionnaire's section list is derived from the distinct
        // section_ids on its items by QuestionnaireSectionList.
        const items = await apiService.fetchQuestionnaireItems(slug);
        loadQuestionnaireItems(slug, items);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === 'string'
          ? detail
          : 'Could not load sections and items.';
        this.contentError = msg;
        this.showToast('error', msg);
      } finally {
        this.loadingContent = false;
      }
    },

    onRetryContent() {
      this.fetchContent();
    },
    // Toggle the page between view mode and edit mode. The toggle is
    // disabled when the questionnaire is locked, so editMode can only
    // ever be true on an unlocked questionnaire.
    onToggleEditMode() {
      if (this.item && !this.item.is_locked) {
        this.editMode = !this.editMode;
      }
    },

    // ---------- metadata edit (name / description / status) ----------
    // The "Edit details" button in the meta-strip area opens this modal.
    // It's only visible in edit mode, so metadata editing follows the
    // same gating philosophy as section/card editing.
    onEditClick() {
      if (this.item && !this.item.is_locked) {
        this.editError = null;
        this.showEdit = true;
      }
    },
    onEditCancel() {
      this.showEdit = false;
      this.editError = null;
    },
    onDeleteClick() {
      if (this.item && !this.item.is_locked) {
        this.deleteError = null;
        this.deleting = false;
        this.confirmText = '';
        this.showDelete = true;
        // Focus the input on the next tick so users can start typing
        // immediately. We deliberately do NOT pre-fill it.
        this.$nextTick(() => {
          const el = this.$refs.confirmInput;
          if (el && typeof el.focus === 'function') el.focus();
        });
      }
    },
    onDeleteCancel() {
      if (this.deleting) return;
      this.showDelete = false;
      this.deleteError = null;
      this.confirmText = '';
    },
    async onEditSubmit(payload) {
      // PATCH /api/seq/questionnaires/:id. Backend ignores slug (immutable
      // post-creation) and is_locked (application-controlled), so even
      // though the form modal sends slug as part of its payload, the
      // server silently drops it. The response is the full row with the
      // trigger-bumped updated_at, which we splice into the shared store.
      this.editError = null;
      const id = this.item.id;
      const patch = {
        name: payload.name,
        description: payload.description,
        status: payload.status,
      };
      try {
        const updated = await apiService.updateQuestionnaire(id, patch);
        replaceQuestionnaire(updated);
        this.showEdit = false;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (Array.isArray(detail)) {
          this.editError = detail.map(d => d.msg).join(' ');
        } else if (typeof detail === 'string') {
          this.editError = detail;
        } else {
          this.editError = 'Failed to update questionnaire. Please try again.';
        }
        // Modal stays open with the error banner; form values preserved.
      }
    },
    async confirmDelete() {
      // DELETE /api/seq/questionnaires/:id. Backend enforces:
      //   - 404 if not found / belongs to another org
      //   - 409 if is_locked is TRUE
      //   - 409 if status is 'archived' (must be un-archived first)
      // The frontend already disables the button when is_locked is TRUE,
      // but the archived case can hit the user since the button is
      // enabled — we surface the backend's message in the modal.
      const id = this.item.id;
      this.deleteError = null;
      this.deleting = true;
      try {
        await apiService.deleteQuestionnaire(id);
        removeQuestionnaire(id);
        this.deleting = false;
        this.showDelete = false;
        this.$router.push('/admin/questionnaires');
      } catch (e) {
        this.deleting = false;
        const detail = e?.response?.data?.detail;
        if (Array.isArray(detail)) {
          this.deleteError = detail.map(d => d.msg).join(' ');
        } else if (typeof detail === 'string') {
          this.deleteError = detail;
        } else {
          this.deleteError = 'Failed to delete questionnaire. Please try again.';
        }
      }
    },
    formatDate(iso) {
      if (!iso) return '';
      return dateFmt.format(new Date(iso));
    },

    // ---------- leave-editor confirm ----------
    onCancelLeave() {
      // User chose to stay. Cancel the pending navigation.
      this.showLeaveConfirm = false;
      if (this.pendingNext) {
        this.pendingNext(false);
        this.pendingNext = null;
      }
    },
    onConfirmLeave() {
      // User confirmed. Drop edit mode, then resume the navigation.
      this.showLeaveConfirm = false;
      this.editMode = false;
      if (this.pendingNext) {
        const next = this.pendingNext;
        this.pendingNext = null;
        next();
      }
    },

    // ---------- translations: download ----------
    onExportJson() {
      // Read-only JSON export of this questionnaire, built client-side
      // from the already-loaded items. See SPEC_questionnaire_json_export.md.
      const exportObj = buildQuestionnaireExport(this.item.slug, this.item.name);
      const json = JSON.stringify(exportObj, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const filename = `${this.item.slug}-export-${today}.json`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // eslint-disable-next-line no-console
      console.log('[export] Questionnaire JSON downloaded:', filename, exportObj);
      this.showToast('success', `Exported: ${filename}`);
    },

    // ---------- translations: upload ----------
    onUploadClick() {
      const el = this.$refs.translationFileInput;
      if (el) el.click();
    },

    async onFileSelected(event) {
      const file = event.target.files?.[0];
      this.resetFileInput();
      if (!file) return;

      let text;
      try {
        text = await file.text();
      } catch (e) {
        this.showToast('error', 'Could not read the file.');
        return;
      }

      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        this.showToast('error', 'File is not valid JSON.');
        return;
      }

      // Structural validation: bail with a clear error if the top-level
      // shape doesn't look like a translation export.
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)
          || typeof parsed.questionnaire !== 'string'
          || !Array.isArray(parsed.sections)
          || !Array.isArray(parsed.cards)) {
        this.showToast('error', 'File structure is invalid. Expected a translation export with questionnaire, sections, and cards.');
        return;
      }

      // The file's questionnaire slug must match the page we're on.
      // Otherwise we'd be applying translations to the wrong content.
      if (parsed.questionnaire !== this.item.slug) {
        this.showToast(
          'error',
          `This file is for questionnaire '${parsed.questionnaire}', not '${this.item.slug}'. Cancel and pick the right file.`,
        );
        return;
      }

      let preview;
      try {
        preview = previewTranslationApply(this.item.slug, parsed);
      } catch (e) {
        this.showToast('error', `Could not analyze file: ${e.message || e}`);
        return;
      }

      this.uploadPreview = { fileData: parsed, ...preview };
    },

    onCancelUploadPreview() {
      this.uploadPreview = null;
    },

    onApplyTranslations() {
      // Future endpoint stub:
      //   POST /api/seq/questionnaires/:slug/translations
      //   body: this.uploadPreview.fileData
      //   response: { sections_updated, cards_updated, fields_changed }
      //   → apiService.applyQuestionnaireTranslations(this.item.slug, fileData)
      if (!this.uploadPreview) return;
      const result = applyTranslationData(this.item.slug, this.uploadPreview.fileData);
      // eslint-disable-next-line no-console
      console.log('[Phase 1] Translations applied:', {
        sections_updated: result.sections_updated,
        cards_updated: result.cards_updated,
        fields_changed: result.changes.length,
        changes: result.changes,
      });
      const n = result.changes.length;
      this.showToast('success', `Translations applied. ${n} ${n === 1 ? 'field' : 'fields'} updated.`);
      this.uploadPreview = null;
    },

    resetFileInput() {
      // Clear the input value so re-selecting the same file fires @change.
      const el = this.$refs.translationFileInput;
      if (el) el.value = '';
    },

    // ---------- toast ----------
    showToast(kind, text) {
      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
        this.toastTimer = null;
      }
      this.toast = { kind, text };
      const ms = kind === 'error' ? 6000 : 4000;
      this.toastTimer = setTimeout(() => {
        this.toast = null;
        this.toastTimer = null;
      }, ms);
    },
  },

  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },

  // In-component navigation guard. Intercepts SPA navigations (clicking
  // a breadcrumb, a menu item, the back button) while edit mode is on,
  // and surfaces a soft confirm. We deliberately do NOT register a
  // beforeunload handler — the spec says browser refresh skips the
  // confirm because edits are live-saved already.
  beforeRouteLeave(to, from, next) {
    if (this.editMode) {
      this.pendingNext = next;
      this.showLeaveConfirm = true;
    } else {
      next();
    }
  },
};
</script>

<style scoped>
.admin-questionnaire-detail-wrap {
  --color-bg-page: #ffffff;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-bad: #C8392E;
  --color-bad-dark: #A92F26;
  --color-bad-soft: rgba(200, 57, 46, 0.10);
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  min-height: 100%;
  box-sizing: border-box;
}

.admin-questionnaire-detail-wrap *,
.admin-questionnaire-detail-wrap *::before,
.admin-questionnaire-detail-wrap *::after {
  box-sizing: border-box;
}

.page {
  max-width: 820px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
}

.breadcrumb {
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
}

.breadcrumb-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  border-bottom: 1px solid transparent;
}

.breadcrumb-link:hover {
  border-bottom-color: var(--color-text-secondary);
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.page-header-text {
  flex: 1;
  min-width: 240px;
}

.page-header-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-top: 4px;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 0.5rem;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 17px;
  color: var(--color-text-secondary);
  margin: 0;
  font-weight: 400;
}

.btn {
  padding: 9px 16px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: opacity 0.15s, background 0.15s, border-color 0.15s, color 0.15s;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.btn-danger-outline {
  background: var(--color-bg-card);
  color: var(--color-bad);
  border-color: var(--color-bad-soft);
}

.btn-danger-outline:hover:not(:disabled) {
  background: var(--color-bad-soft);
  border-color: var(--color-bad);
}

.btn-danger {
  background: var(--color-bad);
  color: white;
  border-color: var(--color-bad);
}

.btn-danger:hover {
  background: var(--color-bad-dark);
  border-color: var(--color-bad-dark);
}

.meta-strip {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 14px 18px;
  background: var(--color-bg-soft);
  border-radius: var(--radius-md);
  border: 0.5px solid var(--color-border);
  /* Compact spacing because the translation-strip sits directly below. */
  margin-bottom: 8px;
  font-size: 13px;
}

/* ───────── Translation strip ───────── */
.translation-strip {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  padding: 8px 14px;
  background: var(--color-bg-soft);
  border-radius: var(--radius-md);
  border: 0.5px solid var(--color-border);
  margin-bottom: 2.5rem;
  font-size: 12px;
}

.translation-label {
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 10px;
  font-weight: 600;
  margin-right: 6px;
}

.translation-sep {
  color: var(--color-text-tertiary);
  font-size: 11px;
  padding: 0 2px;
  user-select: none;
}

.translation-pct {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  padding: 4px 10px;
  border: 0.5px solid transparent;
  border-radius: 4px;
  background: transparent;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.translation-pct:hover:not(:disabled) {
  background: var(--color-bg-card);
  border-color: var(--color-border);
}

.translation-pct:disabled {
  cursor: default;
}

.translation-pct.is-active {
  background: var(--color-bg-card);
  border-color: var(--color-border-strong);
  box-shadow: 0 0 0 1px var(--color-border-strong);
}

.locale-code {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--color-text-secondary);
}

.locale-value {
  font-size: 13px;
  font-weight: 500;
  /* Colour set by the bucket class below. */
}

.translation-pct.pct-complete .locale-value { color: #2e7d32; }
.translation-pct.pct-partial  .locale-value { color: #ef6c00; }
.translation-pct.pct-low      .locale-value { color: #c62828; }

/* ───────── Filter banner (above cards list) ───────── */
.filter-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 14px;
  background: var(--color-accent-soft);
  border: 0.5px solid var(--color-accent);
  border-radius: var(--radius-md);
  margin-bottom: 1rem;
  font-size: 13px;
  color: var(--color-text-primary);
}

.filter-banner-text strong {
  font-weight: 600;
  color: var(--color-accent-dark);
}

.filter-banner-clear {
  background: transparent;
  border: none;
  color: var(--color-accent);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
  padding: 2px 4px;
  text-decoration: underline;
}

.filter-banner-clear:hover {
  color: var(--color-accent-dark);
}


.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-label {
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
  font-weight: 500;
}

.meta-value {
  color: var(--color-text-primary);
  font-weight: 500;
}

.meta-value.mono {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 400;
  color: var(--color-text-secondary);
}

.lock-value .dim {
  color: var(--color-text-tertiary);
}

.status-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 3px 9px;
  border-radius: 4px;
}

.status-pill.status-draft {
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
}

.status-pill.status-active {
  background: var(--color-accent-soft);
  color: var(--color-accent-dark);
}

.status-pill.status-archived {
  background: var(--color-bg-soft);
  color: var(--color-text-tertiary);
}

.section {
  margin-bottom: 2.5rem;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 1rem;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--color-accent);
  display: inline-block;
}

/* Explanatory lead under a section title. The revisions section needs one
   because "revision" means something specific here and the wrong guess is
   expensive. */
.section-intro {
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.55;
  margin: 0 0 1rem;
  max-width: 68ch;
}

.placeholder-area {
  font-size: 14px;
  color: var(--color-text-tertiary);
  background: var(--color-bg-soft);
  border: 0.5px dashed var(--color-border);
  border-radius: var(--radius-md);
  padding: 28px 20px;
  text-align: center;
  margin: 0;
  font-style: italic;
}

.mono-inline {
  font-family: var(--font-mono);
  font-size: 13px;
  background: var(--color-bg-muted);
  padding: 2px 6px;
  border-radius: 4px;
}

.back-link {
  font-size: 14px;
  color: var(--color-accent);
  text-decoration: none;
}

.back-link:hover {
  text-decoration: underline;
}

.loading-state {
  font-size: 14px;
  color: var(--color-text-tertiary);
  padding: 32px 0;
  text-align: center;
  margin: 0;
}

.content-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 20px;
  background: rgba(232, 163, 61, 0.18);
  border: 0.5px solid rgba(232, 163, 61, 0.45);
  border-radius: var(--radius-md);
}

.content-error-text {
  margin: 0;
  font-size: 13px;
  color: #6B4A0F;
  text-align: center;
}

.dim {
  color: var(--color-text-tertiary);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 2000;
  font-family: var(--font-sans);
}

.modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px;
  color: var(--color-text-primary);
}

.modal-text {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 1rem;
  line-height: 1.5;
}

.modal-error {
  font-size: 13px;
  color: #6B1F18;
  background: rgba(200, 57, 46, 0.10);
  border: 0.5px solid rgba(200, 57, 46, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.confirm-target {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 13px;
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
  padding: 1px 8px;
  border-radius: 4px;
  user-select: all;
}

.confirm-input {
  display: block;
  width: 100%;
  margin: 0 0 1.25rem;
  padding: 10px 12px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-primary);
  font-family: var(--font-mono);
  font-size: 13px;
  line-height: 1.4;
  letter-spacing: 0.01em;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

.confirm-input::placeholder {
  color: var(--color-text-tertiary);
  font-family: var(--font-mono);
}

.confirm-input:focus {
  outline: none;
  border-color: var(--color-bad);
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.15);
}

.confirm-input.matched {
  border-color: var(--color-bad);
  background: rgba(200, 57, 46, 0.06);
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.18);
}

.confirm-input:disabled {
  background: var(--color-bg-soft);
  color: var(--color-text-tertiary);
  cursor: not-allowed;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.modal-confirm .modal-footer .btn {
  padding: 10px 16px;
}

/* Header buttons with leading icons (Download / Upload) */
/* Edit button when in edit mode: inverts to accent purple so the user
   sees at a glance "I am editing now" even before scrolling to the
   banner. */
.btn-mode-active {
  background: var(--color-accent) !important;
  color: white !important;
  border-color: var(--color-accent) !important;
}

.btn-mode-active:hover:not(:disabled) {
  background: var(--color-accent-dark) !important;
  border-color: var(--color-accent-dark) !important;
}

/* Small "Edit details" link in the meta-strip, visible only in edit
   mode. Visually quiet — it pulls into view because the meta-strip
   itself is calm. */
.meta-edit-link {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: var(--color-bg-card);
  border: 0.5px solid var(--color-border);
  border-radius: 4px;
  padding: 5px 10px;
  font-family: inherit;
  font-size: 11px;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-weight: 500;
  align-self: center;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}

.meta-edit-link:hover {
  color: var(--color-accent);
  border-color: var(--color-accent);
  background: var(--color-bg-soft);
}

.btn-with-icon {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-with-icon svg {
  flex-shrink: 0;
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}

.hidden-file-input {
  display: none;
}

/* Upload-preview modal: wider than the delete-confirm so the change list
   has room to breathe. */
.modal-upload {
  max-width: 560px;
}

.modal-lock-note {
  font-size: 12px;
  color: var(--color-text-secondary);
  background: var(--color-bg-soft);
  border-left: 3px solid var(--color-accent);
  border-radius: 4px;
  padding: 8px 12px;
  margin: 0 0 12px;
}

.modal-skipped {
  font-size: 12px;
  color: #6B4A0F;
  background: rgba(232, 163, 61, 0.18);
  border: 0.5px solid rgba(232, 163, 61, 0.45);
  border-radius: 4px;
  padding: 8px 12px;
  margin: 0 0 12px;
  line-height: 1.5;
}

.modal-no-changes {
  font-size: 13px;
  color: var(--color-text-tertiary);
  background: var(--color-bg-soft);
  border-radius: 4px;
  padding: 10px 12px;
  margin: 0 0 12px;
  font-style: italic;
}

.modal-details {
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 8px 12px;
  margin: 0 0 12px;
  background: var(--color-bg-soft);
  font-size: 12px;
}

.modal-details summary {
  cursor: pointer;
  font-weight: 500;
  color: var(--color-text-primary);
  padding: 2px 0;
}

.modal-details summary:hover {
  color: var(--color-accent);
}

.change-list {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.change-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 4px 6px;
  border-radius: 3px;
  background: var(--color-bg-card);
  border: 0.5px solid var(--color-border);
  font-size: 11.5px;
}

.change-row.dim {
  background: transparent;
  border: none;
  color: var(--color-text-tertiary);
  font-style: italic;
  justify-content: center;
}

.change-scope {
  flex-shrink: 0;
  color: var(--color-text-secondary);
}

.change-field {
  flex: 1;
  min-width: 0;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.change-action {
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 3px;
}

.change-action.action-added {
  background: var(--color-accent-soft);
  color: var(--color-accent-dark);
}

.change-action.action-changed {
  background: rgba(232, 163, 61, 0.20);
  color: #6B4A0F;
}

.modal-note {
  font-size: 11.5px;
  color: var(--color-text-tertiary);
  margin: 0 0 1.25rem;
  font-style: italic;
}

/* Toast: small fixed-position banner near the bottom of the viewport. */
.toast {
  position: fixed;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  background: var(--color-text-primary);
  color: white;
  padding: 11px 18px;
  border-radius: var(--radius-md);
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  max-width: min(90vw, 520px);
  z-index: 3000;
  font-family: var(--font-sans);
}

.toast-success {
  background: #2E7D32;
}

.toast-error {
  background: var(--color-bad-dark);
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}
</style>
